import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "antd";
import { getResponseErrorMessage, getApiBusinessError, ApiBusinessError } from '@/utils/api-service'
import { isValidImdbId } from '../utils/validation';

import api from "@/api/index";

import { serialsNavigationMain } from '@/constants/serials'
import { navigateSerial } from '@/composable/useRoute'

import AppHeader from "../components/header/AppHeader";
import CardContent from "../components/card/CardContent";
import SerialMainInfo from "../components/serials/SerialMainInfo";
import SerialsSeasons from "../components/serials/SerialsSeasons";
import SerialFiles from "../components/serials/SerialFiles";
import Navigation from "@/components/navigation/Navigation";
import BlockWhiteText from '@components/blocks/BlockWhiteText'

import "../styles/serial-create.scss";

function SerialCreate() {
  const [view, setView] = useState<string>("info");
  const [mainInfo, setMainInfo] = useState<any>({})
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isCheckImdb, setIsCheckImdb] = useState<boolean>(false)

  const navigate = useNavigate();

  const { id } = useParams();

  const handleSave = async() => {
    try {

      if(!isCheckImdb && !mainInfo.IMDB) {
        notification.warning({
          message: 'Не забудьте заполнить IMDB ID'
        })
        setIsCheckImdb(true);
        return false;
      }

      if(mainInfo.IMDB && !isValidImdbId(mainInfo.IMDB)) {
        notification.warning({
          message: 'Неверный формат IMDB ID (пример: tt1234567)'
        })
        return false;
      }

      if(isSaving) {
        return false;
      }

      setIsSaving(true);

      const data = {
        title_original: mainInfo.name_original,
        title: mainInfo.name,
        original_title: mainInfo.name,
        description: mainInfo.description,
        release_year: mainInfo.release_year,
        kinopoisk_id: mainInfo.kinopoiskId,
        imdb_id: mainInfo.IMDB,
        categories: mainInfo.categories.map((category) => category),
        tags: [],
        isActive: mainInfo.isActive ? 1 : 0,
        trailer_url: mainInfo.trailer_url || null
      };

      let message = ''

      if(!id || id === '0') {
        const response = await api.serie.create(data);

        // Бэкенд отдаёт бизнес-отказ с HTTP 200 ({success:false}) — не проглатываем
        const businessError = getApiBusinessError(response);
        if (businessError) {
          throw new ApiBusinessError(businessError);
        }

        const newId = response?.data?.data?.id || null

        if(newId) {
          navigate(`/serial/${newId}`)
        }

        message = 'Сериал успешно создан';

      } else {
        const response = await api.serie.edit(id, data);

        const businessError = getApiBusinessError(response);
        if (businessError) {
          throw new ApiBusinessError(businessError);
        }

        message = 'Сериал успешно сохранен';
      }

      notification.success({
        message: message,
      });

    } catch (err) {
      console.error(err);

      const messageFormatter = getResponseErrorMessage(err, 'Ошибка при сохранении сериала')
      notification.error({
        message: messageFormatter
      });

    } finally {
      setIsSaving(false)
    }
  };

  const handleUpdateMainData = (data:any) => {
    setMainInfo(data)
  }

  const onUpdateNav = (key:string) => {
    const url = navigateSerial(key, id);
    navigate(url);
  }

  const switchView = (action: string) => {
    switch (view) {
      case "info":
        if (action === "prev") {
          navigate("/");
        } else {
          setView("seasons");
        }
        break;

      case "seasons":
        if (action === "prev") {
          setView("info");
        } else {
          setView("files");
        }
        break;

      case "files":
        if (action === "prev") {
          setView("seasons");
        } else {
          notification.success({
            message: "Сериал сохранен",
          });
          navigate("/");
        }
        break;
    }
  };

  const isDisabledSave = useMemo(() => {
    if(!mainInfo.name || !mainInfo.release_year || isSaving) {
      return true
    }

    return false
  }, [mainInfo]);

  const nvaLinks = useMemo(() => {
    return serialsNavigationMain.map((link) => {
        if(link.key === 'serial') {
            return link
        }

        return {
            ...link,
            isDisabled: Number(id) ? false : true
        }
    })
  }, [id])

  const headerTitle = useMemo(() => {
    if(!id) return "Создание сериала";

    return 'Редактирование сериала'
  }, [id])

  return (
    <div className="serial-create app-page">
      <AppHeader title={headerTitle} link={'/'} />

      <div className="app-container serial-create__container">
        {
          id && Number(id) > 0 && <BlockWhiteText text={mainInfo.serealNameTitle || ''} />
        }

        <Navigation defaultActiveLink="serial" links={nvaLinks} update={onUpdateNav}  />

        <div>
          {view === "info" && (
            <CardContent title="Основная информация">
              {view === "info" && <SerialMainInfo update={handleUpdateMainData} id={id} />}
            </CardContent>
          )}

          {view === "files" && <SerialFiles />}
          {view === "seasons" && <SerialsSeasons />}
        </div>

        <div className={"app-footer-control"}>
          <button className="app-btn" onClick={() => switchView("prev")}>
            Отмена
          </button>
          <button 
            className={isDisabledSave ? 'app-btn primary disabled' : 'app-btn primary'}
            disabled={isDisabledSave}
            onClick={() => handleSave()} >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}

export default SerialCreate;
